import { Fragment, useEffect, useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Pencil,
  Plus,
  RefreshCw,
  Shield,
  Trash2,
  X,
} from "lucide-react";
import api, { formatError } from "@/lib/api";
import { PageTitle, Panel } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const EMPTY_FORM = {
  name: "",
  tag: "",
  status: "active",
};

export default function Clan() {
  const { user } = useAuth();
  const canEdit = user?.role !== "viewer";
  const [clans, setClans] = useState([]);
  const [teams, setTeams] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [detailsByClan, setDetailsByClan] = useState({});
  const [detailsLoading, setDetailsLoading] = useState({});
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: clanRows }, { data: teamRows }] = await Promise.all([
        api.get("/clans"),
        api.get("/teams"),
      ]);
      setClans(clanRows);
      setTeams(teamRows);
    } catch (error) {
      toast.error(formatError(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const statsByClan = useMemo(() => {
    const result = {};

    for (const clan of clans) {
      const linkedTeams = teams.filter(
        (team) => team.clan_id === clan.id
      );
      const playerIds = new Set(
        linkedTeams.flatMap((team) => team.player_ids || [])
      );
      const tournamentIds = new Set(
        linkedTeams
          .map((team) => team.tournament_id)
          .filter(Boolean)
      );

      result[clan.id] = {
        teams: linkedTeams.length,
        players: playerIds.size,
        tournaments: tournamentIds.size,
      };
    }

    return result;
  }, [clans, teams]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEdit = (clan) => {
    setEditingId(clan.id);
    setForm({
      name: clan.name || "",
      tag: clan.tag || "",
      status: clan.status || "active",
    });
    setFormOpen(true);
  };

  const closeForm = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormOpen(false);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Nome Clan obbligatorio");
      return;
    }
    if (!form.tag.trim()) {
      toast.error("Tag Clan obbligatorio");
      return;
    }

    const payload = {
      name: form.name.trim(),
      tag: form.tag.trim(),
      status: form.status,
    };

    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/clans/${editingId}`, payload);
        toast.success("Clan aggiornato");
      } else {
        await api.post("/clans", payload);
        toast.success("Clan creato");
      }
      closeForm();
      await load();
    } catch (error) {
      toast.error(formatError(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (clan, status) => {
    try {
      await api.put(`/clans/${clan.id}`, {
        name: clan.name,
        tag: clan.tag,
        status,
      });
      toast.success(
        status === "active"
          ? "Clan riattivato"
          : "Clan disattivato"
      );
      await load();
    } catch (error) {
      toast.error(formatError(error.response?.data?.detail));
    }
  };

  const loadDetails = async (clanId) => {
    setDetailsLoading((current) => ({
      ...current,
      [clanId]: true,
    }));

    try {
      const { data } = await api.get(`/clans/${clanId}/details`);

      setDetailsByClan((current) => ({
        ...current,
        [clanId]: data,
      }));
    } catch (error) {
      toast.error(formatError(error.response?.data?.detail));
    } finally {
      setDetailsLoading((current) => ({
        ...current,
        [clanId]: false,
      }));
    }
  };

  const toggleDetails = async (clanId) => {
    if (expandedId === clanId) {
      setExpandedId(null);
      return;
    }

    setExpandedId(clanId);

    if (!detailsByClan[clanId]) {
      await loadDetails(clanId);
    }
  };

  const deleteClan = async (clan) => {
    const details = detailsByClan[clan.id];
    const stats = details?.stats || statsByClan[clan.id] || {
      teams: 0,
      players: 0,
      tournaments: 0,
      championships: 0,
    };

    const confirmed = window.confirm(
      `Eliminare definitivamente il Clan "${clan.name}"?\n\n` +
        `Verranno eliminati il Clan e i ${stats.teams || 0} Team associati, ` +
        `insieme ai loro risultati, adjustment amministrativi e riferimenti nelle lobby.\n\n` +
        `I Player resteranno nell'anagrafica globale e gli altri Clan, Team e tornei ` +
        `non collegati resteranno intatti. Le classifiche coinvolte verranno ricalcolate.\n\n` +
        `Questa operazione è distruttiva e non può essere annullata.`
    );

    if (!confirmed) {
      return;
    }

    setDeletingId(clan.id);

    try {
      await api.delete(`/clans/${clan.id}`);

      setClans((current) =>
        current.filter((item) => item.id !== clan.id)
      );

      setTeams((current) =>
        current.filter((team) => team.clan_id !== clan.id)
      );

      setDetailsByClan((current) => {
        const next = { ...current };
        delete next[clan.id];
        return next;
      });

      if (expandedId === clan.id) {
        setExpandedId(null);
      }

      toast.success(`Clan "${clan.name}" eliminato`);
    } catch (error) {
      toast.error(formatError(error.response?.data?.detail));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div>
      <PageTitle
        title="Clan"
        subtitle="Anagrafica stabile dei Clan dell'alleanza"
        action={canEdit ? (
          <button
            data-testid="new-clan-btn"
            onClick={openCreate}
            className="btn-gold clip-beveled px-6 py-2.5 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Nuovo Clan
          </button>
        ) : null}
      />

      {canEdit && formOpen && (
        <Panel
          gold
          className="p-6 mb-6 boot-in"
          testid="clan-form"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="heading text-2xl gold-text">
              {editingId ? "Modifica Clan" : "Crea Clan"}
            </h2>
            <button
              onClick={closeForm}
              className="text-[var(--silver)] hover:text-white"
              aria-label="Chiudi"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">
                Nome Clan *
              </label>
              <input
                data-testid="clan-name"
                value={form.name}
                onChange={(event) => setForm({
                  ...form,
                  name: event.target.value,
                })}
                className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white focus:border-[var(--gold)] focus:outline-none"
              />
            </div>

            <div>
              <label className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">
                Tag *
              </label>
              <input
                data-testid="clan-tag"
                value={form.tag}
                maxLength={20}
                onChange={(event) => setForm({
                  ...form,
                  tag: event.target.value,
                })}
                className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white uppercase focus:border-[var(--gold)] focus:outline-none"
              />
            </div>

            <div>
              <label className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">
                Stato
              </label>
              <select
                data-testid="clan-status"
                value={form.status}
                onChange={(event) => setForm({
                  ...form,
                  status: event.target.value,
                })}
                className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white font-rajdhani focus:border-[var(--gold)] focus:outline-none"
              >
                <option value="active">Attivo</option>
                <option value="inactive">Non attivo</option>
              </select>
            </div>
          </div>

          <div className="flex gap-3 mt-5">
            <button
              data-testid="save-clan-btn"
              onClick={save}
              disabled={saving}
              className="btn-gold clip-beveled px-6 py-2 disabled:opacity-50"
            >
              {saving ? "Salvataggio…" : "Salva"}
            </button>
            <button
              onClick={closeForm}
              className="btn-outline-gold clip-beveled px-6 py-2"
            >
              Annulla
            </button>
          </div>
        </Panel>
      )}

      <Panel
        className="overflow-x-auto"
        testid="clans-table"
      >
        <table className="w-full min-w-[760px]">
          <thead>
            <tr className="bg-[#0a0a0a] border-b border-[var(--gold)]/40 font-teko text-lg text-[var(--gold-light)] tracking-wide">
              <th className="text-left px-4 py-3">Clan</th>
              <th className="text-left px-4 py-3">Stato</th>
              <th className="text-center px-4 py-3">Team</th>
              <th className="text-center px-4 py-3">Player</th>
              <th className="text-center px-4 py-3">Tornei</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {clans.map((clan) => {
              const stats = statsByClan[clan.id] || {
                teams: 0,
                players: 0,
                tournaments: 0,
              };

              return (
                <Fragment key={clan.id}>
                <tr
                  key={clan.id}
                  data-testid={`clan-row-${clan.id}`}
                  className="border-b border-[var(--steel)]/20 hover:bg-[#1a1a1a] font-rajdhani"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-[var(--gold)]" />
                      <div>
                        <div className="font-bold text-white">
                          {clan.name}
                        </div>
                        <div className="text-xs text-[var(--gold)]">
                          [{clan.tag}]
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {canEdit ? (
                      <select
                        value={clan.status}
                        onChange={(event) => changeStatus(
                          clan,
                          event.target.value
                        )}
                        className="bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-2 py-1.5 text-xs text-white font-rajdhani focus:border-[var(--gold)] focus:outline-none"
                      >
                        <option value="active">Attivo</option>
                        <option value="inactive">Non attivo</option>
                      </select>
                    ) : (
                      <span className="font-rajdhani text-sm text-[var(--silver)]">
                        {clan.status === "active" ? "Attivo" : "Non attivo"}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center text-white">
                    {stats.teams}
                  </td>
                  <td className="px-4 py-3 text-center text-white">
                    {stats.players}
                  </td>
                  <td className="px-4 py-3 text-center text-white">
                    {stats.tournaments}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        data-testid={`details-clan-${clan.id}`}
                        onClick={() => toggleDetails(clan.id)}
                        className="text-[var(--silver)] hover:text-white flex items-center gap-1 font-rajdhani text-sm"
                      >
                        {expandedId === clan.id ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                        Dettagli
                      </button>

                      {canEdit && (
                        <>
                          <button
                            data-testid={`edit-clan-${clan.id}`}
                            onClick={() => openEdit(clan)}
                            className="text-[var(--gold)] hover:text-[var(--gold-light)] flex items-center gap-1 font-rajdhani text-sm"
                            aria-label={`Modifica ${clan.name}`}
                          >
                            <Pencil className="w-4 h-4" />
                            Modifica
                          </button>

                          <button
                            data-testid={`delete-clan-${clan.id}`}
                            onClick={() => deleteClan(clan)}
                            disabled={deletingId === clan.id}
                            className="text-[var(--destructive)] hover:text-red-400 disabled:opacity-50 flex items-center gap-1 font-rajdhani text-sm"
                          >
                            <Trash2 className="w-4 h-4" />
                            {deletingId === clan.id ? "Eliminazione…" : "Elimina"}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>

                {expandedId === clan.id && (
                  <tr className="border-b border-[var(--steel)]/20 bg-[#0d0d0d]">
                    <td colSpan={6} className="px-4 py-5">
                      {detailsLoading[clan.id] ? (
                        <div className="text-center py-6 font-rajdhani text-[var(--silver)]">
                          Caricamento dettagli…
                        </div>
                      ) : detailsByClan[clan.id] ? (
                        <div className="space-y-5">
                          <div className="flex items-center justify-between gap-3">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 flex-1">
                              <div className="border border-[var(--steel)]/30 px-3 py-2 clip-beveled-sm">
                                <p className="text-[10px] uppercase tracking-widest text-[var(--silver)]">Team</p>
                                <p className="font-oswald text-xl text-white">
                                  {detailsByClan[clan.id].stats?.teams || 0}
                                </p>
                              </div>
                              <div className="border border-[var(--steel)]/30 px-3 py-2 clip-beveled-sm">
                                <p className="text-[10px] uppercase tracking-widest text-[var(--silver)]">Player</p>
                                <p className="font-oswald text-xl text-white">
                                  {detailsByClan[clan.id].stats?.players || 0}
                                </p>
                              </div>
                              <div className="border border-[var(--steel)]/30 px-3 py-2 clip-beveled-sm">
                                <p className="text-[10px] uppercase tracking-widest text-[var(--silver)]">Tornei</p>
                                <p className="font-oswald text-xl text-white">
                                  {detailsByClan[clan.id].stats?.tournaments || 0}
                                </p>
                              </div>
                              <div className="border border-[var(--steel)]/30 px-3 py-2 clip-beveled-sm">
                                <p className="text-[10px] uppercase tracking-widest text-[var(--silver)]">Campionati</p>
                                <p className="font-oswald text-xl text-white">
                                  {detailsByClan[clan.id].stats?.championships || 0}
                                </p>
                              </div>
                            </div>

                            <button
                              onClick={() => loadDetails(clan.id)}
                              className="text-[var(--gold)] hover:text-[var(--gold-light)] flex items-center gap-1 font-rajdhani text-sm"
                            >
                              <RefreshCw className="w-4 h-4" />
                              Aggiorna
                            </button>
                          </div>

                          <div>
                            <h3 className="font-teko text-xl text-[var(--gold-light)] mb-2">
                              Team collegati
                            </h3>
                            <div className="overflow-x-auto">
                              <table className="w-full min-w-[700px]">
                                <thead>
                                  <tr className="border-b border-[var(--steel)]/30 text-xs uppercase tracking-wider text-[var(--silver)] font-rajdhani">
                                    <th className="text-left px-3 py-2">Team</th>
                                    <th className="text-left px-3 py-2">Torneo</th>
                                    <th className="text-left px-3 py-2">Lobby</th>
                                    <th className="text-right px-3 py-2">Player</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {detailsByClan[clan.id].teams?.map((team) => (
                                    <tr key={team.id} className="border-b border-[var(--steel)]/15 font-rajdhani">
                                      <td className="px-3 py-2 text-white">{team.name}</td>
                                      <td className="px-3 py-2 text-white">
                                        {team.tournament_name || "—"}
                                      </td>
                                      <td className="px-3 py-2 text-white">
                                        {team.lobby_name || "—"}
                                      </td>
                                      <td className="px-3 py-2 text-right text-white">
                                        {team.player_ids?.length || 0}
                                      </td>
                                    </tr>
                                  ))}
                                  {detailsByClan[clan.id].teams?.length === 0 && (
                                    <tr>
                                      <td colSpan={4} className="px-3 py-4 text-center text-[var(--silver)] font-rajdhani">
                                        Nessun Team collegato.
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          <div>
                            <h3 className="font-teko text-xl text-[var(--gold-light)] mb-2">
                              Player collegati
                            </h3>
                            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-2">
                              {detailsByClan[clan.id].players?.map((player) => (
                                <div
                                  key={player.id}
                                  className="border border-[var(--steel)]/30 px-3 py-2 clip-beveled-sm font-rajdhani"
                                >
                                  <p className="text-white font-semibold">
                                    {player.activision_id || player.discord_username || player.id}
                                  </p>
                                  <p className="text-xs text-[var(--silver)]">
                                    K/D: {player.kd ?? 0}
                                    {" · "}
                                    Team collegati: {player.team_ids?.length || 0}
                                  </p>
                                </div>
                              ))}
                              {detailsByClan[clan.id].players?.length === 0 && (
                                <p className="text-sm text-[var(--silver)] font-rajdhani">
                                  Nessun Player collegato.
                                </p>
                              )}
                            </div>
                          </div>

                          <div>
                            <h3 className="font-teko text-xl text-[var(--gold-light)] mb-2">
                              Partecipazioni ai tornei
                            </h3>
                            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-2">
                              {detailsByClan[clan.id].tournaments?.map((tournament) => (
                                <div
                                  key={tournament.tournament_id}
                                  className="border border-[var(--steel)]/30 px-3 py-2 clip-beveled-sm font-rajdhani"
                                >
                                  <p className="text-white font-semibold">
                                    {tournament.name || "Torneo"}
                                  </p>
                                  <p className="text-xs text-[var(--silver)]">
                                    Stato: {tournament.state || "—"}
                                    {" · "}
                                    Team: {tournament.teams_count || 0}
                                  </p>
                                </div>
                              ))}
                              {detailsByClan[clan.id].tournaments?.length === 0 && (
                                <p className="text-sm text-[var(--silver)] font-rajdhani">
                                  Nessuna partecipazione.
                                </p>
                              )}
                            </div>
                          </div>

                          <div>
                            <h3 className="font-teko text-xl text-[var(--gold-light)] mb-2">
                              Risultati Campionati
                            </h3>
                            <div className="space-y-2">
                              {detailsByClan[clan.id].championships?.map((championship) => {
                                const result = championship.result;

                                return (
                                  <div
                                    key={championship.championship_id}
                                    className="border border-[var(--gold)]/20 bg-[var(--gold)]/5 px-3 py-3 clip-beveled-sm font-rajdhani"
                                  >
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                      <div>
                                        <p className="text-white font-semibold">
                                          {championship.name || "Campionato"}
                                        </p>
                                        <p className="text-xs text-[var(--silver)]">
                                          Modalità: {championship.mode || "—"}
                                          {" · "}
                                          Stato: {championship.state || "—"}
                                        </p>
                                      </div>

                                      {result ? (
                                        <div className="text-right">
                                          <p className="font-oswald text-xl text-[var(--gold)]">
                                            #{result.rank} · {result.points} punti
                                          </p>
                                          <p className="text-xs text-[var(--silver)]">
                                            Tornei: {result.tournaments}
                                            {" · "}
                                            Team: {result.teams}
                                            {" · "}
                                            Vittorie: {result.wins}
                                            {" · "}
                                            Miglior piazz.: {result.best_placement ?? "—"}
                                          </p>
                                        </div>
                                      ) : (
                                        <p className="text-sm text-[var(--silver)]">
                                          Nessun risultato disponibile.
                                        </p>
                                      )}
                                    </div>

                                    {championship.mode === "balanced" &&
                                      result?.balanced_details?.length > 0 && (
                                        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-2 mt-3">
                                          {result.balanced_details.map((detail) => (
                                            <div
                                              key={detail.tournament_id}
                                              className="border border-[#22d3ee]/20 bg-[#22d3ee]/5 px-3 py-2 clip-beveled-sm"
                                            >
                                              <p className="text-white font-semibold">
                                                {detail.tournament_name || "Torneo"}
                                              </p>
                                              <p className="text-xs text-[var(--silver)] mt-1">
                                                {detail.total_points} punti / {detail.team_count} team
                                              </p>
                                              <p className="text-sm text-[#22d3ee] font-oswald mt-1">
                                                Media: {detail.average_points}
                                              </p>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                  </div>
                                );
                              })}

                              {detailsByClan[clan.id].championships?.length === 0 && (
                                <p className="text-sm text-[var(--silver)] font-rajdhani">
                                  Nessun Campionato associato.
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-6 font-rajdhani text-[var(--silver)]">
                          Dettagli non disponibili.
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
              );
            })}

            {!loading && clans.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-10 text-center font-rajdhani text-[var(--silver)]"
                >
                  Nessun Clan configurato. Creane uno per abilitare
                  il select nelle iscrizioni Discord.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Panel>

      <p className="font-rajdhani text-xs text-[var(--silver)] mt-3">
        I Clan non attivi restano associati ai dati storici, ma non
        vengono mostrati nel select Discord.
      </p>
    </div>
  );
}
